/**
 * El documento, impreso.
 *
 * ── Qué cambia respecto del PDF anterior ────────────────────────────────────
 * El anterior era `doc.text(titulo)` + `autoTable(filas)`. Salía un volcado:
 * sin identidad, sin resumen, sin saber de qué unidad ni de qué periodo era una
 * vez impreso, y sin forma de distinguir un total de una fila cualquiera. Un
 * cliente que recibía eso no podía archivarlo ni enseñárselo a nadie.
 *
 * Esto imprime el MISMO modelo que la pantalla previsualiza, con la identidad de
 * WAYAK: banda del encabezado, resumen de cifras grandes, secciones rotuladas,
 * tablas legibles, notas, totales y un pie con la fecha de emisión y la
 * salvedad legal en todas las páginas.
 *
 * ── Por qué jsPDF a mano y no una plantilla HTML ────────────────────────────
 * `jspdf` y `jspdf-autotable` YA están en el proyecto y ya los usaba la
 * exportación vieja. Imprimir desde HTML pedía `html2canvas` —una dependencia
 * nueva, páginas como imágenes, texto que deja de poder copiarse y peso por
 * diez—. Dibujar las ocho primitivas que hacen falta sale más barato que eso.
 */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { aRgb, arco, barrasDelDia, TINTA, TINTA_FACTURA as F, tintaDeEstado, TONO_FACTURA, TONO_TINTA } from "./geometria";
import { formatoDe, LOGO_WAYAK, resolverImagenes } from "./imagenes";
import type { Imagenes } from "./imagenes";
import type { Bloque, DocumentoReporte, Reloj, Tono } from "./tipos";

const A4 = { ancho: 210, alto: 297 };
const MARGEN = 14;
const ANCHO_UTIL = A4.ancho - MARGEN * 2;
/** Debajo de esto ya no cabe ni un bloque: se cambia de página. */
const PIE_RESERVADO = 22;

interface Lienzo {
  doc: jsPDF;
  y: number;
  /** Diseño del PDF de facturación (`estilo: "factura"`). */
  factura: boolean;
}

const texto = (doc: jsPDF, hex: string) => doc.setTextColor(...aRgb(hex));
const relleno = (doc: jsPDF, hex: string) => doc.setFillColor(...aRgb(hex));
const trazo = (doc: jsPDF, hex: string) => doc.setDrawColor(...aRgb(hex));

/** ¿Cabe `alto` en lo que queda de página? Si no, abre una nueva. */
function asegurar(l: Lienzo, alto: number): void {
  if (l.y + alto <= A4.alto - PIE_RESERVADO) return;
  l.doc.addPage();
  l.y = MARGEN;
}

/**
 * La banda del encabezado.
 *
 * El logotipo se DIBUJA con texto, no se incrusta como imagen: así el PDF pesa
 * kilobytes en vez de megabytes, el nombre se puede buscar y seleccionar, y no
 * hay que cargar un PNG antes de poder exportar.
 */
function encabezado(doc: jsPDF, d: DocumentoReporte, generado: string, imagenes: Imagenes): number {
  if (d.estilo === "factura") return encabezadoFactura(doc, d, generado, imagenes);
  const alto = 30;
  relleno(doc, TINTA.navy);
  doc.rect(0, 0, A4.ancho, alto, "F");
  relleno(doc, TINTA.azul);
  doc.rect(0, alto, A4.ancho, 1.4, "F");

  // WAYAK, con las dos «A» en cian: es el desdoble de la marca.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  let x = MARGEN;
  for (const letra of "WAYAK") {
    texto(doc, letra === "A" ? TINTA.cian : "#FFFFFF");
    doc.text(letra, x, 14);
    x += doc.getTextWidth(letra) + 1.6;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.6);
  texto(doc, "#B7C4D4");
  doc.text("L O G I S T I C S", MARGEN + 0.4, 18.4);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  texto(doc, "#FFFFFF");
  doc.text(d.titulo, A4.ancho - MARGEN, 13, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.6);
  texto(doc, "#9FB3C8");
  doc.text(d.folio, A4.ancho - MARGEN, 18.6, { align: "right" });
  doc.text(d.subtitulo, A4.ancho - MARGEN, 22.6, { align: "right" });

  if (d.sello) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.4);
    const w = doc.getTextWidth(d.sello) + 8;
    relleno(doc, TINTA.verde);
    doc.roundedRect(A4.ancho - MARGEN - w, 25, w, 5.4, 2.7, 2.7, "F");
    texto(doc, "#FFFFFF");
    doc.text(d.sello, A4.ancho - MARGEN - w / 2, 28.7, { align: "center" });
  }

  return alto + 9;
}

/** El mayor tamaño (entre `max` y `min`) con el que `t` cabe en `ancho` mm, con la fuente activa. */
function ajustar(doc: jsPDF, t: string, ancho: number, max: number, min: number): number {
  let pt = max;
  doc.setFontSize(pt);
  while (pt > min && doc.getTextWidth(t) > ancho) {
    pt -= 0.5;
    doc.setFontSize(pt);
  }
  return pt;
}

/**
 * El encabezado con el diseño del PDF de facturación: banda navy, línea azul de
 * marca, título en versales a la derecha y, debajo, la fila de meta con el
 * cliente y la unidad y la fecha de emisión; el sello va como píldora en la banda.
 */
function encabezadoFactura(doc: jsPDF, d: DocumentoReporte, generado: string, imagenes: Imagenes): number {
  const alto = 31;
  relleno(doc, F.navy);
  doc.rect(0, 0, A4.ancho, alto, "F");
  relleno(doc, F.primario);
  doc.rect(0, alto, A4.ancho, 1.06, "F");

  // El logo original (PNG blanco, el mismo del PDF de facturación). Si no se pudo
  // cargar, se dibuja el nombre para que la exportación no se caiga.
  const logo = imagenes.get(LOGO_WAYAK);
  let conLogo = false;
  if (logo) {
    try {
      // El PNG trae ~25 % de margen transparente: 20 mm de alto dejan ~14 mm de logo visible.
      const h = 20;
      doc.addImage(logo, formatoDe(logo), MARGEN, (alto - h) / 2, h * (1080 / 390), h, undefined, "FAST");
      conLogo = true;
    } catch {
      conLogo = false;
    }
  }
  if (!conLogo) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    texto(doc, "#FFFFFF");
    doc.text("WAYAK", MARGEN, 18);
  }

  doc.setFont("helvetica", "bold");
  // El título y el folio se achican (hasta un mínimo) antes de pisar el logo.
  const disponible = A4.ancho - MARGEN * 2 - 20 * (1080 / 390) - 6;
  texto(doc, "#FFFFFF");
  doc.setFontSize(ajustar(doc, d.titulo.toUpperCase(), disponible, 20, 12));
  doc.text(d.titulo.toUpperCase(), A4.ancho - MARGEN, 12.5, { align: "right" });
  doc.setFont("helvetica", "normal");
  texto(doc, F.subtituloBanda);
  doc.setFontSize(ajustar(doc, d.folio, disponible, 10, 7));
  doc.text(doc.splitTextToSize(d.folio, disponible)[0], A4.ancho - MARGEN, 19, { align: "right" });

  // El sello, como píldora de estado bajo el folio.
  if (d.sello) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    const w = doc.getTextWidth(d.sello) + 10;
    relleno(doc, F.primario);
    doc.roundedRect(A4.ancho - MARGEN - w, 22.2, w, 6.6, 3.3, 3.3, "F");
    texto(doc, "#FFFFFF");
    doc.text(d.sello, A4.ancho - MARGEN - w / 2, 26.6, { align: "center" });
  }

  // Fila de meta, bajo la banda.
  const y = alto + 9;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  texto(doc, F.tenue);
  doc.text("CLIENTE · UNIDAD", MARGEN, y);
  doc.text("FECHA DE EMISIÓN", MARGEN + 112, y);
  doc.setFont("helvetica", "bold");
  texto(doc, F.texto);
  doc.setFontSize(ajustar(doc, d.subtitulo || "—", 106, 11, 8));
  doc.text(doc.splitTextToSize(d.subtitulo || "—", 106)[0], MARGEN, y + 4.8);
  doc.setFontSize(11);
  doc.text(generado, MARGEN + 112, y + 4.8);

  return y + 15;
}

/** El rótulo de una sección: versales, azul, con su filo debajo. */
function rotulo(l: Lienzo, titulo: string): void {
  asegurar(l, 12);
  const { doc } = l;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(l.factura ? 8.5 : 7.4);
  texto(doc, l.factura ? F.tenue : TINTA.azul);
  // `charSpace` va en mm: 0.18 mm ≈ 0.5 pt, el espaciado del comprobante.
  doc.text(titulo.toUpperCase(), MARGEN, l.y, { charSpace: l.factura ? 0.18 : 0.5 });
  l.y += 2.2;
  trazo(doc, l.factura ? F.borde : TINTA.linea);
  doc.setLineWidth(0.2);
  doc.line(MARGEN, l.y, A4.ancho - MARGEN, l.y);
  l.y += 5;
}

/** Texto pequeño que explica el bloque de arriba. Devuelve lo que creció. */
function pie(l: Lienzo, t: string): void {
  const { doc } = l;
  doc.setFont("helvetica", "normal");
  const paso = l.factura ? 3.6 : 3;
  doc.setFontSize(l.factura ? 7.5 : 6.6);
  texto(doc, l.factura ? F.tenue : TINTA.tenue);
  const lineas = doc.splitTextToSize(t, ANCHO_UTIL);
  asegurar(l, lineas.length * paso + 2);
  doc.text(lineas, MARGEN, l.y + 2.4);
  l.y += lineas.length * paso + (l.factura ? 5 : 3);
}

function bloqueResumen(l: Lienzo, datos: { etiqueta: string; valor: string; nota?: string; tono?: Tono }[]): void {
  const alto = 19;
  asegurar(l, alto + 5);
  const { doc } = l;
  relleno(doc, l.factura ? F.tarjeta : "#F5F8FC");
  trazo(doc, l.factura ? F.borde : "#DCE6F2");
  doc.setLineWidth(0.2);
  doc.roundedRect(MARGEN, l.y, ANCHO_UTIL, alto, l.factura ? 2.8 : 2, l.factura ? 2.8 : 2, "FD");

  const ancho = ANCHO_UTIL / datos.length;
  datos.forEach((dato, i) => {
    const x = MARGEN + ancho * i + 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(l.factura ? 6.3 : 5.8);
    texto(doc, l.factura ? F.tenue : TINTA.gris);
    doc.text(dato.etiqueta.toUpperCase(), x, l.y + 6, { charSpace: l.factura ? 0.05 : 0.3 });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(l.factura ? ajustar(doc, dato.valor, ancho - 7, 14, 9) : 12);
    texto(doc, l.factura ? (dato.tono && dato.tono !== "neutral" ? TONO_FACTURA[dato.tono] : F.navy) : TINTA.navy);
    doc.text(dato.valor, x, l.y + 13);
    if (dato.nota) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(l.factura ? ajustar(doc, dato.nota, ancho - 7, 6.6, 5) : 5.4);
      texto(doc, l.factura ? F.tenue : TINTA.tenue);
      doc.text(dato.nota, x, l.y + 16.6);
    }
  });
  l.y += alto + 6;
}

/** Un reloj: el aro y, dentro de la tarjeta, su etiqueta y su valor. */
function dibujarReloj(doc: jsPDF, r: Reloj, cx: number, cy: number, radio: number): void {
  const { modo } = arco(r.fraccion, r.valor, radio);
  const color = TONO_TINTA[r.tono ?? "info"];

  doc.setLineWidth(1.1);
  trazo(doc, "#E7EBF1");
  doc.circle(cx, cy, radio, "S");

  if (modo === "vacio") return;
  trazo(doc, color);
  if (modo === "lleno") {
    doc.circle(cx, cy, radio, "S");
    return;
  }
  // jsPDF no dibuja arcos parciales, así que se aproxima con segmentos cortos.
  // Doce por vuelta bastan a este radio: el ojo no distingue la poligonal.
  const f = Math.max(0, Math.min(1, r.fraccion ?? 0));
  const pasos = Math.max(1, Math.round(f * 48));
  const total = f * Math.PI * 2;
  for (let i = 0; i < pasos; i++) {
    const a1 = -Math.PI / 2 + (total * i) / pasos;
    const a2 = -Math.PI / 2 + (total * (i + 1)) / pasos;
    doc.line(
      cx + radio * Math.cos(a1), cy + radio * Math.sin(a1),
      cx + radio * Math.cos(a2), cy + radio * Math.sin(a2),
    );
  }
}

function bloqueRelojes(l: Lienzo, relojes: Reloj[]): void {
  const porFila = 4;
  const ancho = ANCHO_UTIL / porFila;
  const alto = l.factura ? 20 : 17;
  const filas = Math.ceil(relojes.length / porFila);
  asegurar(l, filas * alto + 4);
  const { doc } = l;

  relojes.forEach((r, i) => {
    const fila = Math.floor(i / porFila);
    const col = i % porFila;
    const x = MARGEN + ancho * col;
    const y = l.y + alto * fila;

    if (l.factura) {
      relleno(doc, F.tarjeta);
      trazo(doc, F.borde);
    } else {
      trazo(doc, TINTA.linea);
    }
    doc.setLineWidth(0.2);
    doc.roundedRect(x + 1, y, ancho - 2, alto - 2.5, l.factura ? 2.8 : 1.6, l.factura ? 2.8 : 1.6, l.factura ? "FD" : "S");
    dibujarReloj(doc, r, x + 8, y + (alto - 2.5) / 2, 4.2);

    const tx = x + 14.5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(l.factura ? 6.6 : 5.2);
    texto(doc, l.factura ? F.tenue : TINTA.gris);
    doc.text(doc.splitTextToSize(r.etiqueta.toUpperCase(), ancho - 17)[0], tx, y + (l.factura ? 6 : 5.4), { charSpace: l.factura ? 0.05 : 0.2 });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(l.factura ? 11 : 9);
    texto(doc, l.factura ? F.navy : TINTA.navy);
    doc.text(r.valor ?? "—", tx, y + (l.factura ? 11.4 : 10.4));
    if (r.nota) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(l.factura ? 6.2 : 4.8);
      texto(doc, l.factura ? F.tenue : TINTA.tenue);
      doc.text(doc.splitTextToSize(r.nota, ancho - 17)[0], tx, y + (l.factura ? 15 : 13.4));
    }
  });
  l.y += filas * alto + 2;
}

const FOTO = { lado: 22, hueco: 5 };

/**
 * Los campos como la tarjeta «Información del pago» del PDF de facturación:
 * tarjeta a todo el ancho, título en versales y los renglones repartidos en dos
 * columnas (se leen de arriba abajo en la primera y luego en la segunda), con la
 * etiqueta a la izquierda de cada valor.
 */
function tarjetaDeCampos(
  l: Lienzo,
  titulo: string | undefined,
  campos: { etiqueta: string; valor: string; mono?: boolean }[],
): void {
  const { doc } = l;
  const filaAlto = 6.4;
  const hueco = 6;
  const interior = ANCHO_UTIL - 10;

  // Dos columnas, como la tarjeta del comprobante, mientras la etiqueta más larga
  // y el valor más largo quepan uno junto al otro; si no, una sola columna: un
  // valor cortado con «…» en un documento que sirve para deslindar es peor que
  // una tarjeta un poco más alta.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const anchoEtiqueta = Math.max(...campos.map((c) => doc.getTextWidth(c.etiqueta))) + 3;
  doc.setFont("helvetica", "bold");
  const anchoValor = Math.max(...campos.map((c) => doc.getTextWidth(c.valor)));
  const dosColumnas = campos.length > 1 && anchoEtiqueta + anchoValor <= (interior - hueco) / 2;

  const porCol = dosColumnas ? Math.ceil(campos.length / 2) : campos.length;
  const anchoCol = dosColumnas ? (interior - hueco) / 2 : interior;
  const cabecera = titulo ? 11 : 4;
  const alto = cabecera + porCol * filaAlto + 2;
  asegurar(l, alto + 5);

  relleno(doc, F.tarjeta);
  trazo(doc, F.borde);
  doc.setLineWidth(0.2);
  doc.roundedRect(MARGEN, l.y, ANCHO_UTIL, alto, 2.8, 2.8, "FD");

  if (titulo) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    texto(doc, F.tenue);
    doc.text(titulo.toUpperCase(), MARGEN + 5, l.y + 6.4, { charSpace: 0.18 });
  }

  campos.forEach((c, i) => {
    const col = Math.floor(i / porCol);
    const x = MARGEN + 5 + col * (anchoCol + hueco);
    const y = l.y + cabecera + 1.5 + (i % porCol) * filaAlto;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    texto(doc, F.tenue);
    doc.text(c.etiqueta, x, y + 2.6);
    doc.setFont("helvetica", "bold");
    texto(doc, F.texto);
    doc.text(doc.splitTextToSize(c.valor, anchoCol - anchoEtiqueta - 2)[0], x + anchoEtiqueta + 2, y + 2.6);
  });
  l.y += alto + 6;
}

function bloqueCampos(
  l: Lienzo,
  campos: { etiqueta: string; valor: string; mono?: boolean }[],
  retrato?: string,
): void {
  // Con retrato, los campos se corren a su derecha y la rejilla se angosta: la
  // alternativa —foto encima y campos debajo— gastaba el doble de alto para
  // decir lo mismo.
  const sangria = retrato ? FOTO.lado + FOTO.hueco : 0;
  const util = ANCHO_UTIL - sangria;
  const porFila = retrato ? 2 : 3;
  const ancho = util / porFila;
  const alto = 9.5;
  const filas = Math.ceil(campos.length / porFila);
  asegurar(l, Math.max(filas * alto, retrato ? FOTO.lado : 0) + 3);
  const { doc } = l;

  if (retrato) {
    try {
      doc.addImage(retrato, formatoDe(retrato), MARGEN, l.y, FOTO.lado, FOTO.lado, undefined, "FAST");
      trazo(doc, TINTA.linea);
      doc.setLineWidth(0.3);
      doc.roundedRect(MARGEN, l.y, FOTO.lado, FOTO.lado, 1.5, 1.5, "S");
    } catch {
      // Un retrato ilegible no vale una exportación rota: se sigue sin él.
    }
  }

  campos.forEach((c, i) => {
    const x = MARGEN + sangria + ancho * (i % porFila);
    const y = l.y + alto * Math.floor(i / porFila);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.4);
    texto(doc, TINTA.gris);
    doc.text(c.etiqueta.toUpperCase(), x, y + 3, { charSpace: 0.25 });
    doc.setFont(c.mono ? "courier" : "helvetica", "bold");
    doc.setFontSize(c.mono ? 7 : 8);
    texto(doc, TINTA.texto);
    doc.text(doc.splitTextToSize(c.valor, ancho - 3)[0], x, y + 7.4);
  });
  l.y += Math.max(filas * alto, retrato ? FOTO.lado : 0) + 3;
}

function bloqueTabla(l: Lienzo, b: Extract<Bloque, { tipo: "tabla" }>): void {
  const tope = b.topePdf ?? 2000;
  const filas = b.filas.slice(0, tope);
  const recortadas = b.filas.length - filas.length;

  // Con muchas columnas (el consumo por unidad lleva nueve, el kilometraje siete) la letra del comprobante
  // parte los encabezados a media palabra: se baja un punto y medio y se aprieta el relleno.
  const densa = l.factura && b.columnas.length >= 7;

  autoTable(l.doc, {
    startY: l.y,
    margin: { left: MARGEN, right: MARGEN, bottom: PIE_RESERVADO },
    head: [b.columnas.map((c) => c.titulo.toUpperCase())],
    body: filas.map((f) => b.columnas.map((c) => (f[c.clave] ?? "—").toString())),
    styles: {
      fontSize: l.factura ? (densa ? 7 : 8.5) : 6.4, cellPadding: l.factura ? (densa ? 1.6 : 2.4) : 1.5,
      textColor: aRgb(l.factura ? F.texto : TINTA.texto),
      lineColor: aRgb(l.factura ? F.bordeSuave : TINTA.lineaSuave), lineWidth: 0.1,
    },
    headStyles: l.factura
      ? { fillColor: aRgb(F.navy), textColor: [255, 255, 255], fontStyle: "bold", fontSize: densa ? 6.4 : 8.5, cellPadding: densa ? 2 : 2.8 }
      : { fillColor: aRgb(TINTA.azulClaro), textColor: aRgb(TINTA.navy), fontStyle: "bold", fontSize: 5.8, cellPadding: 2 },
    alternateRowStyles: { fillColor: aRgb(l.factura ? F.tarjeta : TINTA.cebra) },
    columnStyles: Object.fromEntries(
      b.columnas.map((c, i) => [i, {
        halign: c.alinea === "derecha" ? "right" : c.alinea === "centro" ? "center" : "left",
        font: c.mono && !l.factura ? "courier" : "helvetica",
      }]),
    ),
  });
  // `lastAutoTable` lo publica el propio plugin; no hay otra forma de saber
  // dónde terminó la tabla después de que repaginó sola.
  l.y = ((l.doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? l.y) + (l.factura ? 7 : 4);

  if (recortadas > 0) {
    pie(l, `Se imprimen las primeras ${filas.length.toLocaleString("es-MX")} filas de ` +
           `${b.filas.length.toLocaleString("es-MX")}. El detalle completo va en el Excel: ` +
           `un PDF con ${b.filas.length.toLocaleString("es-MX")} renglones no se consulta, se archiva.`);
  }
}

function bloqueRegla(l: Lienzo, b: Extract<Bloque, { tipo: "regla" }>): void {
  const { doc } = l;

  // La leyenda, una vez, antes de los días.
  asegurar(l, 7);
  let lx = MARGEN;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(l.factura ? 7.5 : 5.8);
  for (const item of b.leyenda) {
    relleno(doc, tintaDeEstado(item.estado));
    doc.roundedRect(lx, l.y - 2, 2.4, 2.4, 0.5, 0.5, "F");
    texto(doc, l.factura ? F.tenue : TINTA.gris);
    doc.text(item.etiqueta, lx + 3.6, l.y);
    lx += doc.getTextWidth(item.etiqueta) + 11;
  }
  l.y += l.factura ? 6 : 5;

  for (const dia of b.dias) {
    asegurar(l, l.factura ? 19 : 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(l.factura ? 8.5 : 6.6);
    texto(doc, l.factura ? F.texto : TINTA.navy);
    doc.text(dia.etiqueta, MARGEN, l.y);
    if (dia.cifras) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(l.factura ? 7.5 : 5.8);
      texto(doc, l.factura ? F.tenue : TINTA.gris);
      doc.text(dia.cifras, A4.ancho - MARGEN, l.y, { align: "right" });
    }
    l.y += 1.8;
    const altoBarra = l.factura ? 5 : 4.4;

    relleno(doc, l.factura ? F.bordeSuave : "#EEF1F5");
    doc.roundedRect(MARGEN, l.y, ANCHO_UTIL, altoBarra, 0.6, 0.6, "F");
    for (const barra of barrasDelDia(dia.tramos, dia.inicio)) {
      relleno(doc, tintaDeEstado(barra.estado));
      doc.rect(
        MARGEN + (barra.izquierda / 100) * ANCHO_UTIL, l.y,
        Math.max((barra.ancho / 100) * ANCHO_UTIL, 0.4), altoBarra, "F",
      );
    }
    // La línea base de las horas va 2.8 mm POR DEBAJO del borde inferior de la
    // barra. Con menos, el texto se mete dentro de la barra y las horas quedan
    // escritas encima de los tramos — que es como salió la primera vez.
    l.y += altoBarra + (l.factura ? 3.4 : 2.8);

    doc.setFont(l.factura ? "helvetica" : "courier", "normal");
    doc.setFontSize(l.factura ? 6.5 : 5.2);
    texto(doc, l.factura ? F.tenue : TINTA.gris);
    for (let h = 0; h <= 24; h += 3) {
      doc.text(String(h).padStart(2, "0"), MARGEN + (h / 24) * ANCHO_UTIL, l.y, {
        align: h === 0 ? "left" : h === 24 ? "right" : "center",
      });
    }
    l.y += l.factura ? 5 : 4;
  }
  l.y += 1;
}

function bloqueNota(l: Lienzo, b: Extract<Bloque, { tipo: "nota" }>): void {
  const { doc } = l;
  const color = (l.factura ? TONO_FACTURA : TONO_TINTA)[b.tono ?? "aviso"];
  const cuerpoPt = l.factura ? 8.5 : 6.8;
  const paso = l.factura ? 3.8 : 3.1;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(cuerpoPt);
  const cuerpo = doc.splitTextToSize(b.texto, ANCHO_UTIL - 10);
  const alto = cuerpo.length * paso + (b.titulo ? 5 : 0) + 6;
  asegurar(l, alto + 3);

  if (l.factura) {
    // Tarjeta como las del comprobante; el tono sólo se queda en el filo izquierdo.
    relleno(doc, F.tarjeta);
    trazo(doc, F.borde);
    doc.setLineWidth(0.2);
    doc.roundedRect(MARGEN, l.y, ANCHO_UTIL, alto, 2.8, 2.8, "FD");
    relleno(doc, color);
    doc.rect(MARGEN + 0.1, l.y + 2, 0.9, alto - 4, "F");
  } else {
    relleno(doc, "#FFF9E8");
    doc.rect(MARGEN, l.y, ANCHO_UTIL, alto, "F");
    relleno(doc, color);
    doc.rect(MARGEN, l.y, 1, alto, "F");
  }

  let y = l.y + 4.6;
  if (b.titulo) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(cuerpoPt);
    texto(doc, l.factura ? F.texto : "#8A6D1F");
    doc.text(b.titulo, MARGEN + 4, y);
    y += l.factura ? 4.8 : 4.4;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(cuerpoPt);
  texto(doc, l.factura ? F.textoSecundario : "#6B5A2E");
  doc.text(cuerpo, MARGEN + 4, y);
  l.y += alto + 4;
}

function bloqueTotal(l: Lienzo, b: Extract<Bloque, { tipo: "total" }>): void {
  asegurar(l, 16);
  const { doc } = l;
  relleno(doc, l.factura ? F.navy : TINTA.azul);
  doc.roundedRect(MARGEN, l.y, ANCHO_UTIL, 11, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  texto(doc, "#FFFFFF");
  doc.text(b.etiqueta.toUpperCase(), MARGEN + 5, l.y + 7, { charSpace: l.factura ? 0.18 : 0.4 });
  doc.setFontSize(11);
  doc.text(b.valor, A4.ancho - MARGEN - 5, l.y + 7.4, { align: "right" });
  l.y += 15;
}

/**
 * El pie, en TODAS las páginas y al final.
 *
 * Va al final a propósito: hasta que no se dibujó el último bloque no se sabe
 * cuántas páginas hay, y «Página 1 de 1» impreso en un documento de cuatro
 * páginas es de las cosas que hacen dudar del resto del documento.
 */
function pieDePagina(doc: jsPDF, generado: string, factura: boolean): void {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    const y = A4.alto - 13;
    if (factura) {
      trazo(doc, F.borde);
      doc.setLineWidth(0.2);
      doc.line(MARGEN, y, A4.ancho - MARGEN, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      texto(doc, F.tenue);
      doc.text(
        "Reporte de operación emitido por WAYAK Logistics. No constituye dictamen pericial ni prueba judicial por sí solo.",
        MARGEN, y + 4, { maxWidth: ANCHO_UTIL },
      );
      doc.text(`WAYAK Logistics · Generado el ${generado} · Página ${p} de ${total}`, A4.ancho - MARGEN, y + 8.8, { align: "right" });
      continue;
    }
    trazo(doc, TINTA.linea);
    doc.setLineWidth(0.2);
    doc.line(MARGEN, y, A4.ancho - MARGEN, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    texto(doc, TINTA.gris);
    doc.text(`WAYAK Logistics · Generado el ${generado}`, MARGEN, y + 4);
    texto(doc, TINTA.tenue);
    doc.text(
      "Reporte de operación emitido por WAYAK Logistics. No constituye dictamen pericial ni prueba judicial por sí solo.",
      MARGEN, y + 7.4,
    );
    texto(doc, TINTA.gris);
    doc.text(`Página ${p} de ${total}`, A4.ancho - MARGEN, y + 4, { align: "right" });
  }
}

/**
 * Los caracteres que las fuentes de jsPDF no saben dibujar.
 *
 * ── Cómo se encontró ───────────────────────────────────────────────────────
 * El resumen del historial de posiciones decía «PARADAS (≥5 MIN)» y en el PDF
 * salía **«PARADAS ("e 5 MIN)»**. Las fuentes estándar de jsPDF van en WinAnsi,
 * que no tiene `≥`: cada byte fuera del juego sale como la basura que le toque,
 * sin error y sin aviso. Se vio abriendo el archivo, no corriendo las pruebas.
 *
 * ── Por qué aquí y no en cada texto ────────────────────────────────────────
 * Hay una treintena de llamadas a `doc.text` y los textos no son todos
 * nuestros: el nombre de una unidad, una dirección geocodificada o el nombre de
 * un conductor los escribe alguien más. Vigilar treinta sitios es garantizar
 * que el trigésimo primero se escape. Se envuelve `doc.text` UNA vez, al
 * construir, y queda cubierto todo lo que pase por ahí.
 *
 * Se traduce a un equivalente legible en vez de borrar: «>=» dice lo que decía
 * «≥», y un hueco no.
 */
const EQUIVALENTES: Record<string, string> = {
  "≥": ">=", "≤": "<=", "≠": "!=", "≈": "~",
  "→": "->", "←": "<-", "↑": "^", "↓": "v", "∞": "inf", "√": "raiz",
  "•": "·", "✓": "si", "✗": "no", "™": "(TM)", "№": "No.",
  // Los espacios raros: duro, fino y estrecho. Se ven igual que un espacio y
  // no lo son, así que pasarían por el filtro como «?» en mitad de una cifra.
  "\u00a0": " ", "\u202f": " ", "\u2009": " ", "\u2011": "-",
};

/*
  Lo que NO está en esta tabla porque WinAnsi sí lo dibuja: «±», «×», «÷», «°»,
  «€», las comillas tipográficas, la raya y los puntos suspensivos. Se
  comprobó: están todos dentro de Latin-1 o entre los que jsPDF mapea.
*/

/** Lo que WinAnsi dibuja sin mancharse: Latin-1 más la puntuación habitual. */
const DIBUJABLE = /[\u0020-\u007e\u00a1-\u00ff\u2018\u2019\u201c\u201d\u2013\u2014\u2026\u00b7\u20ac]/;

export function saneado(v: string): string {
  let fuera = false;
  for (const c of v) {
    if (!DIBUJABLE.test(c)) { fuera = true; break; }
  }
  if (!fuera) return v;
  return [...v]
    .map((c) => (DIBUJABLE.test(c) ? c : (EQUIVALENTES[c] ?? "?")))
    .join("");
}

/** Envuelve `doc.text` para que nada sin dibujo llegue a la hoja. */
function conTextoSaneado(doc: jsPDF): void {
  const original = doc.text.bind(doc);
  (doc as unknown as { text: typeof doc.text }).text = ((
    t: string | string[],
    ...resto: unknown[]
  ) =>
    original(
      Array.isArray(t) ? t.map(saneado) : typeof t === "string" ? saneado(t) : t,
      ...(resto as [number, number]),
    )) as typeof doc.text;
}

/** Arma el PDF en memoria. Separado de la descarga para poder probarlo. */
export function construirPdf(
  d: DocumentoReporte,
  generado: string,
  imagenes: Imagenes = new Map(),
): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  conTextoSaneado(doc);
  const l: Lienzo = { doc, y: encabezado(doc, d, generado, imagenes), factura: d.estilo === "factura" };

  for (const b of d.bloques) {
    switch (b.tipo) {
      case "resumen": bloqueResumen(l, b.datos); break;
      case "relojes":
        if (b.titulo) rotulo(l, b.titulo);
        bloqueRelojes(l, b.relojes);
        if (b.pie) pie(l, b.pie);
        break;
      case "campos": {
        const retrato = b.foto ? imagenes.get(b.foto) : undefined;
        if (l.factura && !retrato) { tarjetaDeCampos(l, b.titulo, b.campos); break; }
        if (b.titulo) rotulo(l, b.titulo);
        bloqueCampos(l, b.campos, retrato);
        break;
      }
      case "tabla":
        // Título + encabezado + unas filas: sin ese espacio la tabla empieza al
        // fondo de la hoja con sólo el encabezado y sigue en la página siguiente.
        if (l.factura) asegurar(l, 40);
        if (b.titulo) rotulo(l, b.titulo);
        bloqueTabla(l, b);
        if (b.pie) pie(l, b.pie);
        break;
      case "regla":
        if (b.titulo) rotulo(l, b.titulo);
        bloqueRegla(l, b);
        if (b.pie) pie(l, b.pie);
        break;
      case "nota": bloqueNota(l, b); break;
      case "total": bloqueTotal(l, b); break;
      case "salto": doc.addPage(); l.y = MARGEN; break;
    }
  }

  pieDePagina(doc, generado, l.factura);
  return doc;
}

/**
 * Baja la hoja.
 *
 * Es asíncrona porque antes de imprimir hay que resolver los retratos. Cuando
 * el documento no trae ninguno, `resolverImagenes` devuelve de inmediato y esto
 * se comporta igual que antes.
 */
export async function descargarPdf(d: DocumentoReporte, generado: string): Promise<void> {
  const imagenes = await resolverImagenes(d);
  construirPdf(d, generado, imagenes).save(`${d.archivo}.pdf`);
}

/**
 * La misma hoja, pero como `blob:` para enseñarla antes de bajarla.
 *
 * El cliente final no abre una descarga en el teléfono: cae en una carpeta y
 * ahí se queda. Su pantalla de reportes enseña el PDF en un visor y sólo lo
 * guarda si era el que quería, igual que hace con los reportes que genera el
 * API. Quien llama se encarga de soltar la URL con `revokeObjectURL`.
 */
export async function pdfComoUrl(
  d: DocumentoReporte,
  generado: string,
): Promise<{ url: string; nombre: string }> {
  const imagenes = await resolverImagenes(d);
  const doc = construirPdf(d, generado, imagenes);
  return {
    url: URL.createObjectURL(doc.output("blob")),
    nombre: `${d.archivo}.pdf`,
  };
}
